import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import Grid from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';
import { ESCALATION_TYPE_LABELS, MonitoringMethod } from 'core/constants';

import { JSONSchemaValue } from 'utils/api/modules/incidents/incidents.types';
import { EscalationProfileResponse } from 'utils/api/modules/alarms/alarms.types';

import Chip from 'components/Chip';

import VatixMonitoring from 'components/Icons/VatixMonitoring';

import SelfMonitoring from 'components/Icons/SelfMonitoring';

import { VatixError } from 'components/Error/types';

import { colors } from 'theme/colors';

import ActionCell from '../ActionCell';
import MonitoringTypeOperator from '../MonitoringTypeOperator';

const basicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    valueGetter: (_value: unknown, row: EscalationProfileResponse) => ({
      name: row.name,
      escalationType: row.escalationType,
    }),
    renderCell: ({ value }: { value: { name: string; escalationType: string } }) => (
      <Chip
        label={value.name}
        colors={{ background: 'transparent', foreground: colors.text.primary }}
        size="medium"
        variant="outlined"
        iconLeft={value.escalationType === MonitoringMethod.ArcMonitored ? <VatixMonitoring /> : <SelfMonitoring />}
        iconLeftGapWidth={6}
      />
    ),
  },
  {
    field: 'escalationType',
    headerName: 'Monitoring type',
    renderCell: ({ value }: { value: JSONSchemaValue<EscalationProfileResponse> }) => (
      <>{ESCALATION_TYPE_LABELS[value as unknown as MonitoringMethod]}</>
    ),
    filterOperators: MonitoringTypeOperator,
    nativeFilter: 'escalation_type',
    sortingField: 'escalation_type',
  },
  {
    field: 'numberOfUsers',
    headerName: 'Number of users',
    filterable: false,
    // ToDo: implement sorting on backend
    sortable: false,
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: '',
    maxWidth: 50,
    ariaLabel: 'actions_cell',
    renderCell: ({ row, colDef }: { row: EscalationProfileResponse; colDef: ColumnDefinition }) => (
      <ActionCell rowData={row} actions={colDef.actions} />
    ),
    sortable: false,
  },
] as unknown as ColumnDefinition[];

const EscalationProfileList: React.FunctionComponent<{
  onError: (error: VatixError) => void;
}> = ({ onError }) => {
  const { routing } = useStore();

  const onRowClick: GridEventListener<'rowClick'> = (item: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.alarms.escalationProfiles.details, { escalationProfileId: item.id }));
  };

  return (
    <Grid
      basicColumns={basicColumns}
      onError={onError}
      onRowClick={onRowClick}
      dataURL="/api/alarms/escalation-profiles/"
      disableSelectAllColumns
      disableColumnSelector
    />
  );
};

export default observer(EscalationProfileList);
